import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchRetiredPriests } from '@/services/api';
import { Church, Heart, Phone, Quote, Award, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SeoHead from '@/components/Seo'; // Ensure this is imported

const RetiredPriests = () => {
  const [retiredPriests, setRetiredPriests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRetiredPriests = async () => {
      try {
        const response = await fetchRetiredPriests();
        setRetiredPriests(response.data.priests);
      } catch (error) {
        console.error('Error fetching retired priests:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRetiredPriests();
  }, []);

  // --- SEO BLOCK ---
  // This stays "on" regardless of loading state
  const seoContent = (
    <SeoHead
      title="Roll of Honour & Memorial | Ibadan South Diocese"
      description="Celebrating the legacy and faithful service of our retired and departed clergy. Explore the history of leadership in the Diocese of Ibadan South."
      ogImage="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/home_ss1tdy.jpg"
    />
  );

  if (loading) return (
    <>
      {seoContent}
      <div className="flex flex-col justify-center items-center min-h-screen bg-[#fcfaf7]">
        <div className="h-12 w-12 border-4 border-amber-600/10 border-t-amber-600 rounded-full animate-spin mb-4" />
        <div className="animate-pulse text-slate-400 font-serif italic text-lg">Preparing the Roll of Honour...</div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-24">
      {seoContent}
      
      <div className="max-w-7xl mx-auto px-6">
        {/* Elegant Header */}
        <div className="text-center mb-20">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-block px-4 py-1.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.4em] rounded-full mb-6">
            Legacy & Remembrance
          </motion.div>
          <h1 className="text-5xl lg:text-7xl font-serif font-bold text-slate-900 mb-6">
            Roll of <span className="text-amber-600">Honour</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto font-serif italic leading-relaxed">
            Celebrating the faithful stewards who have finished their active course and those who have transitioned to the Church Triumphant.
          </p>
          {/* ... Legend and Grid continue below ... */}
          <div className="mt-10 flex justify-center gap-8 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-amber-500" /> Retired Clergy</div>
            <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-slate-300" /> Of Blessed Memory</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {retiredPriests.map((priest, index) => {
            const isDeceased = priest.deceased === 1;
            const activeParish = priest.parishes?.[0];

            return (
              <motion.div
                key={priest.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`group relative overflow-hidden border-none transition-all duration-700 rounded-[2.5rem] shadow-sm ${
                  isDeceased 
                    ? 'bg-slate-50 hover:bg-white border-slate-100' 
                    : 'bg-white hover:shadow-2xl hover:shadow-amber-900/5 ring-1 ring-slate-100'
                }`}>
                  {/* ... Rest of your Priest Card UI ... */}
                  <div className={`absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 rounded-full blur-3xl transition-opacity group-hover:opacity-100 opacity-20 ${
                    isDeceased ? 'bg-slate-300' : 'bg-amber-400'
                  }`} />

                  <div className="p-8 md:p-10">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8">
                      <div className="relative shrink-0">
                        <div className={`w-32 h-32 md:w-40 md:h-40 rounded-3xl overflow-hidden shadow-inner transition-all duration-700 ${
                          isDeceased ? 'grayscale group-hover:grayscale-0 scale-95 group-hover:scale-100' : 'scale-100 group-hover:scale-105'
                        }`}>
                          <img 
                            src={priest.photo || "https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png"} 
                            alt={priest.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {isDeceased ? (
                          <div className="absolute -bottom-2 -right-2 bg-slate-900 text-white p-2 rounded-2xl shadow-xl">
                            <Heart className="h-4 w-4 fill-white" />
                          </div>
                        ) : (
                          <div className="absolute -bottom-2 -right-2 bg-amber-600 text-white p-2 rounded-2xl shadow-xl">
                            <Sparkles className="h-4 w-4" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 text-center sm:text-left">
                        <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 ${
                          isDeceased ? 'text-slate-400' : 'text-amber-600'
                        }`}>
                          {isDeceased ? 'Of Blessed Memory' : 'Honourably Retired'}
                        </div>
                        <h2 className={`text-2xl md:text-3xl font-serif font-bold leading-tight mb-4 ${
                          isDeceased ? 'text-slate-700' : 'text-slate-900'
                        }`}>
                          {priest.title} {priest.name}
                        </h2>
                        
                        <div className="space-y-4">
                          <div className="flex items-center justify-center sm:justify-start gap-3">
                            <div className={`p-1.5 rounded-lg ${isDeceased ? 'bg-slate-100' : 'bg-amber-50'}`}>
                              <Church className={`h-4 w-4 ${isDeceased ? 'text-slate-400' : 'text-amber-600'}`} />
                            </div>
                            <div className="text-left">
                              <p className="text-[10px] font-black uppercase text-slate-300 leading-none mb-1">Final Service</p>
                              <p className="text-sm font-bold text-slate-600 truncate max-w-[200px]">
                                {activeParish?.name || "Diocesan Headquarters"}
                              </p>
                            </div>
                          </div>

                          {priest.phone && !isDeceased && (
                            <a href={`tel:${priest.phone}`} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-amber-600 hover:text-white transition-all rounded-xl text-xs font-bold text-slate-600">
                              <Phone size={14} />
                              Contact Office
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    {priest.biography && (
                      <div className="mt-8 pt-8 border-t border-slate-100 relative">
                        <Quote className={`absolute top-6 left-0 h-8 w-8 opacity-5 ${isDeceased ? 'text-slate-900' : 'text-amber-600'}`} />
                        <p className="text-slate-500 font-serif italic leading-relaxed text-base pl-6">
                          "{priest.biography.substring(0, 180)}..."
                        </p>
                      </div>
                    )}

                    <div className="mt-8 flex items-center justify-between">
                       <div className="flex items-center gap-2">
                          <Award className={`h-4 w-4 ${isDeceased ? 'text-slate-300' : 'text-amber-500'}`} />
                          <span className="text-[10px] font-black uppercase tracking-tighter text-slate-400">
                            Ref: IDS-{priest.id.toString().padStart(3, '0')}
                          </span>
                       </div>
                       {isDeceased ? (
                         <span className="text-[10px] font-black uppercase text-slate-300 tracking-widest bg-slate-100 px-3 py-1 rounded-full">
                           Memorial
                         </span>
                       ) : (
                        <span className="text-[10px] font-black uppercase text-amber-700 tracking-widest bg-amber-100 px-3 py-1 rounded-full">
                           Active Emeritus
                        </span>
                       )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RetiredPriests;